import classNames from 'classnames';
import { memo, useId } from 'react';
import InputMask from 'react-input-mask';
import NumberFormat from 'react-number-format';
import { Button } from 'antd';
import CustomInput from '../customInput';
import { errorIcon } from '../assets';
import '../style.scss';
import './legacyStyle.scss';
import type { TextInputProps } from './types';
import { useTextInput } from './useTextInput';
import { generateOptionNumberInput, generateOptionInputMask } from './textInput.handler';

/**
 * Placeholder callback standing in for the text input's optional handlers. Takes no arguments and performs no work.
 *
 * @returns Nothing.
 */
const NOOP = () => {};

/**
 * Labeled text field that switches between masked, numeric and plain input variants. Renders InputMask for the date, four-digit and phone masks, NumberFormat with the decimal separator and scale when isNumberFormat, and CustomInput otherwise, then appends the error block with its optional icon and link, the caution text, and a warning block whose link is opened by onClickWarningLink from useTextInput.
 *
 * @param props - Field name, value and label, mask and number-format flags, error and warning state with their messages and links, plus the change and key handlers.
 * @returns The rendered text input wrapper element.
 */
const TextInput = (props: TextInputProps) => {
  const {
    className = undefined,
    isDateMask = false,
    isFourDigitsMask = false,
    isPhoneNumberMask = false,
    type = 'text',
    placeholder = '',
    label = '',
    value = '',
    disabled = false,
    isError = false,
    errorMessage = '',
    isWarning = false,
    warningMessage = '',
    warningLinkText = '',
    warningLink = '',
    onChange: onChangeProp = NOOP,
    optionCheckOut = undefined,
    classNameLabel = '',
    id = '',
    onKeyDown = NOOP,
    isNumberFormat = false,
    messageBoxContent = '',
    linkText = '',
    handleClickLinkText = NOOP,
    cautionText = null,
    suffix = null,
    decimalSeparator = '.',
    decimalScale = 1,
    isKeyboardNumberMobile = false,
    hasErrorIcon = true,
    name,
  } = props;
  const { onChange, onClickWarningLink } = useTextInput(props);
  const generatedId = useId();
  const resolvedId = id || generatedId;

  return (
    <div
      id={`text-input-wrapper-${id}`}
      className={classNames('custom-text-input', className, suffix ? '--suffix' : '')}
    >
      {!!messageBoxContent && messageBoxContent}
      {!!label && (
        <label
          htmlFor={resolvedId}
          className={classNames('custom-text-input__label', classNameLabel)}
        >
          {label}
        </label>
      )}
      {optionCheckOut}
      {isDateMask || isFourDigitsMask || isPhoneNumberMask ? (
        <InputMask
          className={classNames(
            'custom-text-input__input',
            'w-100',
            type === 'textarea' ? '--textarea' : '',
            isError ? '--error' : '',
          )}
          name={name}
          type={isDateMask || isFourDigitsMask ? 'text' : 'tel'}
          placeholder={placeholder}
          mask={isDateMask ? '99/99/9999' : isFourDigitsMask ? '9999' : '999-999-9999'}
          maskChar={null}
          value={value}
          onChange={(e: any) => onChange(name, e.target.value)}
          id={resolvedId}
          onKeyDown={onKeyDown}
          disabled={disabled}
          {...generateOptionInputMask({ isKeyboardNumberMobile })}
        />
      ) : isNumberFormat ? (
        <div className="relative">
          <NumberFormat
            getInputRef={props.inputRef}
            id={resolvedId}
            className={classNames('custom-text-input__input w-100', isError ? '--error' : '')}
            placeholder={placeholder}
            name={name}
            onValueChange={({ value: newValue }) => {
              onChangeProp(name, newValue);
            }}
            value={value}
            disabled={disabled}
            allowNegative={false}
            isNumericString
            decimalSeparator={decimalSeparator as any}
            onKeyDown={onKeyDown}
            decimalScale={decimalScale}
            {...generateOptionNumberInput({
              isKeyboardNumberMobile,
              decimalSeparator,
            })}
          />
          {suffix && <div className="custom-text-input__suffix">{suffix}</div>}
        </div>
      ) : (
        <CustomInput
          size="middle"
          id={resolvedId}
          suffix={suffix as any}
          name={name}
          type={type as any}
          disabled={disabled}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          onKeyDown={onKeyDown}
          isError={isError as any}
        />
      )}
      {isError && !!errorMessage && (
        <div
          className={classNames(
            'custom-text-input__error-container',
            linkText ? 'flex justify-between items-center' : '',
            hasErrorIcon ? 'flex has-error-icon' : '',
          )}
        >
          {hasErrorIcon && (
            <img className="icon-error" src={errorIcon} alt="Exclamation circle icon" />
          )}
          <div className="custom-text-input__error-message">{errorMessage}</div>
          {!!linkText && (
            <Button
              className="custom-select-input__warning-button"
              type="link"
              onClick={handleClickLinkText}
            >
              {linkText}
            </Button>
          )}
        </div>
      )}
      {!!cautionText && cautionText}
      {isWarning && (
        <div className="custom-text-input__warning-container">
          <div className="custom-text-input__warning-message">{warningMessage}</div>
          <Button
            type="link"
            onClick={() => {
              onClickWarningLink(warningLink);
            }}
          >
            {warningLinkText}
          </Button>
        </div>
      )}
    </div>
  );
};

export default memo(TextInput);
